import Splide from '@splidejs/splide';
import '@splidejs/splide/css/core';
import { uniqueCarouselName, nearestHeadingText, carouselA11yOptions } from '../components/carousel-a11y';
import '../../scss/wp-blocks/wp-block-query-carousel.scss';

/**
 * Carousel Query — Splide integration for the "carousel-query" Query Loop variation.
 *
 * The variation adds `.wp-block-query--carousel` to the root element.
 * On the frontend the post-template renders as:
 *
 *   <div class="wp-block-query wp-block-query--carousel">
 *     <ul class="wp-block-post-template">
 *       <li class="wp-block-post">…</li>
 *       …
 *     </ul>
 *   </div>
 *
 * This script wraps the markup with the Splide HTML structure and mounts the carousel.
 * The controls container (.splide__arrows-pagination-container) mirrors the structure
 * used by the ACF carousel block (acf/carousel) so that shared CSS applies.
 *
 * @see https://splidejs.com/guides/getting-started/
 */

/**
 * Creates a fill-style progress bar inside the --fill pagination container
 * and updates it on every slide change.
 *
 * @param {Splide} splide - The Splide instance (before mount).
 */
function initFillPagination( splide ) {
	const container = splide.root.querySelector( '.splide__pagination--fill' );
	if ( ! container ) return;

	const bar = document.createElement( 'li' );
	bar.className = 'splide__pagination--fill__bar';
	container.appendChild( bar );

	const update = () => {
		const total = splide.Components.Slides.getLength( false );
		const index = splide.index;
		const perPage = splide.options.perPage || 1;
		const slidesViewed = index + perPage;
		const progress = Math.min( 1, slidesViewed / total );
		bar.style.width = `${ progress * 100 }%`;
	};

	splide.on( 'mounted move', update );
}

/**
 * Builds the Splide DOM structure around the existing post-template <ul>.
 *
 * Result (mirrors acf/carousel PHP output):
 *   <div class="splide">  (role="region" + aria-label messi da Splide, opzione label)
 *     <div class="splide__track">
 *       <ul class="splide__list wp-block-post-template …">
 *         <li class="splide__slide wp-block-post">…</li>
 *       </ul>
 *     </div>
 *     <div class="splide__arrows-pagination-container">
 *       <ul class="splide__pagination splide__pagination--fill"></ul>
 *       <div class="splide__arrows"></div>
 *     </div>
 *   </div>
 *
 * BUGFIX: Gestisce il wrapper <div> creato dal layout grid.
 * Se post-template ha un layout grid abilitato, WordPress lo renderizza come:
 *   <div class="wp-block-post-template">
 *     <ul>...</ul>
 *   </div>
 * In questo caso estraiamo la <ul> e rimuoviamo il wrapper.
 *
 * @param {HTMLElement} query - The .wp-block-query--carousel element.
 * @returns {HTMLElement|null} The .splide root ready to mount, or null.
 */
function buildSplideStructure( query ) {
	let list = query.querySelector( '.wp-block-post-template' );
	if ( ! list ) return null;

	// BUGFIX: Se wp-block-post-template è un <div> (layout grid è abilitato),
	// estrarre la <ul> che si trova dentro e rimuovere il wrapper div
	if ( list.tagName === 'DIV' ) {
		const ul = list.querySelector( 'ul' );
		if ( ! ul ) return null;

		// Muovere la <ul> fuori dal div wrapper
		list.parentNode.insertBefore( ul, list );
		// Rimuovere il div wrapper
		list.remove();
		// Usare la <ul> come lista Splide
		list = ul;
	}

	// Tag every <li> as a Splide slide.
	[ ...list.children ].forEach( ( li ) => {
		li.classList.add( 'splide__slide' );
	} );

	// Re-purpose the <ul> as the Splide list.
	list.classList.add( 'splide__list' );

	// Create .splide__track wrapper.
	const track = document.createElement( 'div' );
	track.className = 'splide__track';
	list.parentNode.insertBefore( track, list );
	track.appendChild( list );

	// Create the controls container (same structure as acf/carousel).
	const controlsContainer = document.createElement( 'div' );
	controlsContainer.className = 'splide__arrows-pagination-container';

	// Pagination (fill bar).
	const pagination = document.createElement( 'ul' );
	pagination.className = 'splide__pagination splide__pagination--fill';
	controlsContainer.appendChild( pagination );

	// Arrows container (Splide injects the buttons here).
	const arrows = document.createElement( 'div' );
	arrows.className = 'splide__arrows';
	controlsContainer.appendChild( arrows );

	// Create .splide root wrapper.
	const splideRoot = document.createElement( 'div' );
	splideRoot.className = 'splide';
	track.parentNode.insertBefore( splideRoot, track );
	splideRoot.appendChild( track );
	splideRoot.appendChild( controlsContainer );

	return splideRoot;
}

/**
 * Splide options for the default "carousel-query" variation (landscape posts).
 *
 * La prima card (focus 0) parte dal bordo sinistro, distanziata dello stesso
 * spazio che separa le card (padding left = gap): si intravede solo la card
 * successiva a destra, non la precedente.
 */
const DEFAULT_GAP = '1.3333333em';

const OPTIONS_DEFAULT = {
	type      : 'loop',
	height    : 'auto',
	perPage   : 3,
	perMove   : 1,
	arrows    : true,
	focus     : 0,
	start     : 0,
	gap       : DEFAULT_GAP,
	padding   : {
		left : DEFAULT_GAP,
		right: '20%',
	},
	omitEnd   : true,
	pagination: false,
	arrowPath : 'M2.052 20H37.949M20 2.051 37.949 20 20 37.949',
	breakpoints: {
		1440: {
			padding   : {
				left : DEFAULT_GAP,
				right: '15%',
			},
		},
		1080: {
			perPage: 2,
			padding   : {
				left : DEFAULT_GAP,
				right: '10%',
			},
		},
		768: {
			perPage: 1,
			padding   : {
				left : DEFAULT_GAP,
				right: '7.5%',
			},
		},
	},
};

/**
 * Splide options for the "carousel-query-team" variation (portrait team cards).
 */
const OPTIONS_TEAM = {
	type      : 'slide',
	height    : 'auto',
	perPage   : 3,
	perMove   : 1,
	arrows    : true,
	focus     : 0,
	start     : 0,
	gap       : '1.3333333em',
	padding   : {
		left : '15%',
		right: '15%',
	},
	omitEnd   : true,
	pagination: false,
	arrowPath : 'M2.052 20H37.949M20 2.051 37.949 20 20 37.949',
	breakpoints: {
		1440: {
			padding   : {
				left : '15%',
				right: '15%',
			},
		},
		1080: {
			perPage: 2,
			padding   : {
				left : '10%',
				right: '10%',
			},
		},
		768: {
			perPage: 1,
			gap       : '1em',
			padding   : {
				left : '1.333em',
				right: '1.333em',
			},
		},
	},
};

/**
 * Initialise carousel-query instances on the page.
 */
function initQueryCarousels() {
	const queries = document.querySelectorAll( '.wp-block-query--carousel, .wp-block-query--carousel-team' );

	queries.forEach( ( query ) => {
		const splideRoot = buildSplideStructure( query );
		if ( ! splideRoot ) return;

		const isTeam  = query.classList.contains( 'wp-block-query--carousel-team' );
		const options = isTeam ? OPTIONS_TEAM : OPTIONS_DEFAULT;

		// Nome accessibile: aria-label del Query Loop se impostato, altrimenti il
		// titolo che lo precede (es. "News"). Finisce su root e frecce.
		const name   = uniqueCarouselName( query.getAttribute( 'aria-label' ) || nearestHeadingText( query ) );
		const splide = new Splide( splideRoot, { ...options, ...carouselA11yOptions( name ) } );

		// Init fill pagination (same as acf/carousel).
		initFillPagination( splide );

		splide.mount();
	} );
}

document.addEventListener( 'DOMContentLoaded', initQueryCarousels );
